import { Calendar, ListChecks } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { FieldServiceContract } from '@Models';
import { isKnownAvailabilityMode, UPPERCASE_LABEL_CLASS_NAME } from '../constants';

interface AvailabilitySectionProps {
  contract: FieldServiceContract;
}

const NOT_CONFIGURED_KEY = 'FieldService.NotConfigured';

/** A placeholder reads as absence, so it drops the weight a real value carries. */
const valueClass = (isSet: boolean) => (isSet ? 'text-sm font-bold text-gray-900' : 'text-sm italic text-gray-500');

const AvailabilitySection = ({ contract }: AvailabilitySectionProps) => {
  const { t } = useTranslation();

  const { availability_mode: mode, visits_per_period: visits } = contract;

  let availabilityHeadline: string;
  // Null hides the subtitle: it describes a frequency, so it says nothing while there is none.
  let availabilitySub: string | null;
  if (mode === null) {
    availabilityHeadline = t(NOT_CONFIGURED_KEY);
    availabilitySub = null;
  } else if (!isKnownAvailabilityMode(mode)) {
    availabilityHeadline = mode;
    availabilitySub = t('FieldService.Availability.Sub');
  } else {
    // Exhaustive over FieldServiceAvailabilityMode ('daily' | 'weekly' | 'monthly'): adding a
    // mode to that type without a case here is a real TS error, not a silent gap.
    switch (mode) {
      case 'daily':
        availabilityHeadline = t('FieldService.Availability.Daily');
        availabilitySub = t('FieldService.Availability.DailySub');
        break;
      // A mode can be set before its count is. i18next renders the plural with a blank
      // where {{count}} goes, so the bare label stands in until the count arrives.
      case 'weekly':
        availabilityHeadline =
          visits === null
            ? t('FieldService.Availability.Weekly')
            : t('FieldService.Availability.WeeklyHeadline', { count: visits });
        availabilitySub = t('FieldService.Availability.Sub');
        break;
      case 'monthly':
        availabilityHeadline =
          visits === null
            ? t('FieldService.Availability.Monthly')
            : t('FieldService.Availability.MonthlyHeadline', { count: visits });
        availabilitySub = t('FieldService.Availability.Sub');
        break;
      default: {
        // Unreachable while every FieldServiceAvailabilityMode has a case above. If that stops
        // being true, `unhandled` is no longer typed `never` and this is a compile error.
        const unhandled: never = mode;
        throw new Error(`Unhandled availability mode: ${unhandled}`);
      }
    }
  }

  const { visit_task_target_min: taskMin, visit_task_target_max: taskMax } = contract;
  const taskTargetLabel = taskMin === null || taskMax === null ? null : `${taskMin}–${taskMax}`;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6">
      {/* Visits per period has no card of its own: it only means something for weekly and
          monthly, where the headline below already states it. */}
      <div>
        <span className={cn('block', UPPERCASE_LABEL_CLASS_NAME)}>{t('FieldService.Sections.Availability')}</span>
        <p className="mb-3 mt-2 text-xs text-gray-500">{t('FieldService.Contract.TaskVolumeDesc')}</p>
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <ListChecks size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <div className={valueClass(taskTargetLabel !== null)}>{taskTargetLabel ?? t(NOT_CONFIGURED_KEY)}</div>
            <div className="text-xs text-gray-500">{t('FieldService.Contract.TasksPerVisit')}</div>
          </div>
        </div>
      </div>

      <div>
        <p className="mb-3 text-xs text-gray-500">{t('FieldService.Sections.AvailabilityDesc')}</p>
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Calendar size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <div className={valueClass(mode !== null)}>{availabilityHeadline}</div>
            {availabilitySub && <div className="text-xs text-gray-500">{availabilitySub}</div>}
          </div>
        </div>
        {contract.lead_time_days !== null && (
          <p className="mt-2 text-xs text-gray-500">
            {t('FieldService.Availability.LeadTime', { count: contract.lead_time_days })}
          </p>
        )}
      </div>
    </div>
  );
};

export default AvailabilitySection;
